import React, {ReactElement} from "react"
import {AllowedAdjustmentsColumns, AdjustmentsColumns} from "./Adjestments.types"
import {Adjustment} from "../../resources/adjustment"
import {Resource} from "../../resources/common"
import {useNavigate} from "react-router-dom"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import {OrgName} from "../OrgName/OrgName"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import classNames from "classnames"
import {GetCustomer} from "./Helpers"
import {StatusTypeValue} from "../../resources/adjustment"
import {UserName} from "../Users/UserName"
import {ActionButtons} from "./ActionButtons"
import numeral from "numeral"
import moment from "moment"
import {getCustomerName} from "../../resources/customer"
import IdLink from "../IdLink/IdLink"

interface AdjustmentRowProps {
	adjustment: Adjustment
	refresh: () => void
	included?: Resource[]
	includedColumns: Array<AllowedAdjustmentsColumns>
}

export default function AdjustmentRow({adjustment, refresh, included, includedColumns}: AdjustmentRowProps) {
	const navigate = useNavigate()
	const navigateWithListPath = useNavigateWithListPath()
	const customerId = adjustment.relationships.customer?.data.id
	const status = adjustment.attributes.statusHistory.slice(-1)[0]
	const statusType = status.type

	const contentColumns: Partial<Record<AllowedAdjustmentsColumns, ReactElement>> = {}

	if (includedColumns.includes(AdjustmentsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink className="data-table-id" href={`/adjustments/${adjustment.id}`}>
					{adjustment.id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.org)) {
		contentColumns["Org"] = adjustment.relationships.org?.data.id ? (
			<OrgName orgId={adjustment.relationships.org.data.id.toString()} included={included} />
		) : (
			<DataTableCell />
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.account)) {
		contentColumns["Account"] = (
			<DataTableCell>
				{" "}
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (adjustment.relationships.account.data.id) {
							navigate(`/accounts/${adjustment.relationships.account.data.id}`)
						}
					}}
				>
					{adjustment.relationships.account.data.id}
				</a>{" "}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.counterAccount)) {
		contentColumns["Counter Account"] = adjustment.relationships.counterAccount?.data.id ? (
			<DataTableCell>
				{" "}
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (adjustment.relationships.counterAccount?.data.id) {
							navigate(`/accounts/${adjustment.relationships.counterAccount.data.id}`)
						}
					}}
				>
					{adjustment.relationships.counterAccount.data.id}
				</a>{" "}
			</DataTableCell>
		) : (
			<DataTableCell />
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.customer)) {
		contentColumns["Customer"] = customerId ? (
			<DataTableCell>
				{" "}
				{GetCustomer(
					String(customerId),
					(customer) => (
						<a
							className="link"
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
							}}
						>
							{getCustomerName(customer)}
						</a>
					),
					included
				)}{" "}
			</DataTableCell>
		) : (
			<DataTableCell />
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.direction)) {
		contentColumns["Direction"] = <DataTableCell> {adjustment.attributes.direction} </DataTableCell>
	}

	if (includedColumns.includes(AdjustmentsColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell> {numeral(adjustment.attributes.amount / 100).format("$0,0.00")} </DataTableCell>
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.status)) {
		contentColumns["Status"] = <DataTableCell> {StatusTypeValue(statusType)} </DataTableCell>
	}

	if (includedColumns.includes(AdjustmentsColumns.user)) {
		contentColumns["User"] = (
			<UserName
				userId={status.byUserId.toString()}
				onResult={(user) => <DataTableCell>{user.attributes.username}</DataTableCell>}
				included={included}
			/>
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.description)) {
		contentColumns["Description"] = <DataTableCell> {adjustment.attributes.description} </DataTableCell>
	}

	if (includedColumns.includes(AdjustmentsColumns.note)) {
		contentColumns["Note"] = <DataTableCell> {adjustment.attributes.internalNote} </DataTableCell>
	}

	if (includedColumns.includes(AdjustmentsColumns.createdAt)) {
		contentColumns["Created At"] = (
			<DataTableCell> {moment(adjustment.attributes.createdAt).format("L LT")} </DataTableCell>
		)
	}

	if (includedColumns.includes(AdjustmentsColumns.action)) {
		contentColumns["Action"] = (
			<DataTableCell>
				{" "}
				<ActionButtons adj={adjustment} refresh={refresh} />{" "}
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigateWithListPath(`/adjustments/${adjustment.id}`)
			}}
		>
			{content}
		</DataTableRow>
	)
}
